import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  HistoryOutlined as HistoryIcon,
  ContentCopy as CopyIcon,
  CheckCircleOutline as SyncedIcon,
  BookmarkBorderOutlined as SavedIcon,
  DeleteOutline as ClearIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, customShadows, borderRadiusPx } from "../../../../theme";
import { CommonTable, CommonChip, CommonButton, toast } from "../../ui";

/**
 * TaxHistoryTable - Audit trail table for tax adjustments and regulatory updates.
 */
const TaxHistoryTable = ({
  history = [],
  onClearHistory,
  currency = "POL",
}) => {
  const [copiedId, setCopiedId] = React.useState(null);

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Address copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const columns = [
    {
      id: "version",
      label: "Ref / Version",
      render: (val, row, index) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[800], fontSize: "0.82rem" }}>
          {row.version || `v${history.length - index}.0`}
        </Typography>
      ),
    },
    {
      id: "timestamp",
      label: "Date & Time",
      render: (val) => (
        <Box>
          <Typography variant="body2" sx={{ color: colors.slate[800], fontWeight: 500, fontSize: "0.82rem" }}>
            {val ? moment(val).format("MMM DD, YYYY") : "Recent"}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.slate[400], fontSize: "0.75rem" }}>
            {val ? moment(val).format("hh:mm A") : ""}
          </Typography>
        </Box>
      ),
    },
    {
      id: "adminAddress",
      label: "Updated By",
      render: (val, row) => {
        if (!val) return <Typography variant="caption" sx={{ color: colors.slate[400] }}>Platform Admin</Typography>;
        const shortAddr = `${val.substring(0, 6)}...${val.substring(val.length - 4)}`;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: colors.slate[700] }}>
              {shortAddr}
            </Typography>
            <Tooltip title={copiedId === row.id ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={() => handleCopy(val, row.id)} sx={{ p: 0.25 }}>
                <CopyIcon sx={{ fontSize: 13, color: colors.slate[400] }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      id: "winnerTaxRate",
      label: "Winner Tax",
      render: (val) => (
        <CommonChip
          label={`${Number(val || 0).toFixed(1)}%`}
          variant="soft"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.78rem",
            color: colors.primary.main,
            bgcolor: colors.primary.lighter,
          }}
        />
      ),
    },
    {
      id: "platformTaxRate",
      label: "Platform Tax",
      render: (val) => (
        <CommonChip
          label={`${Number(val || 0).toFixed(1)}%`}
          variant="soft"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.78rem",
            color: colors.purple[700],
            bgcolor: colors.purple[50],
          }}
        />
      ),
    },
    {
      id: "creationFee",
      label: "Creation Fee",
      render: (val) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[700], fontSize: "0.82rem" }}>
          {val || "10.00"} {currency}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (val, row) => {
        const isSynced = row.isSynced || val === "Synced On-Chain";
        return (
          <CommonChip
            label={isSynced ? "Synced On-Chain" : "Saved Locally"}
            icon={isSynced ? <SyncedIcon sx={{ fontSize: 14 }} /> : <SavedIcon sx={{ fontSize: 14 }} />}
            variant="soft"
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: "0.74rem",
              color: isSynced ? colors.success.dark : colors.amber[800],
              bgcolor: isSynced ? colors.success.background : colors.amber[100],
            }}
          />
        );
      },
    },
    {
      id: "notes",
      label: "Regulatory Memo / Notes",
      render: (val) => (
        <Typography
          variant="caption"
          sx={{
            color: colors.slate[500],
            fontSize: "0.78rem",
            maxWidth: 220,
            display: "inline-block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {val || "Standard policy revision"}
        </Typography>
      ),
    },
  ];

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          borderBottom: `1px solid ${colors.dashboard.divider}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: colors.slate[100],
              color: colors.slate[700],
            }}
          >
            <HistoryIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                color: colors.dashboard.title,
              }}
            >
              Tax Policy Audit Log & Change History
            </Typography>
            <Typography variant="caption" sx={{ color: colors.slate[400] }}>
              Chronological log of all statutory tax and fee parameter modifications.
            </Typography>
          </Box>
        </Stack>

        {history && history.length > 0 && onClearHistory && (
          <CommonButton
            variant="text"
            size="small"
            color="error"
            onClick={onClearHistory}
            startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: "0.78rem" }}
          >
            Clear History
          </CommonButton>
        )}
      </Box>

      {/* Table Content */}
      <CardContent sx={{ p: 0 }}>
        <CommonTable
          columns={columns}
          rows={history}
          rowKey="id"
          emptyMessage="No historical tax adjustments recorded yet."
        />
      </CardContent>
    </Card>
  );
};

TaxHistoryTable.propTypes = {
  history: PropTypes.array,
  onClearHistory: PropTypes.func,
  currency: PropTypes.string,
};

export default TaxHistoryTable;
